import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { fetchOrgDisplayNames } from '../services/adminAPI';
import { normalizeOrgSlug } from '../utils/tokenUtils';

export const OrganisationContext = createContext();

const decodeJwt = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Failed to decode JWT:', error);
        return null;
    }
};

export const OrganisationProvider = ({ children }) => {
    const { token, loading: authLoading, isAuthenticated } = useAuth();
    const [organisations, setOrganisations] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [activeOrgId, setActiveOrgId] = useState(null);
    const [activeRoleId, setActiveRoleId] = useState(null);
    const [selectorOpen, setSelectorOpen] = useState(false);

    const openSelector = () => setSelectorOpen(true);
    const closeSelector = () => setSelectorOpen(false);

    // Clear stale org/role state once the session is gone (e.g. idle timeout), so a
    // lingering profile card or selector dropdown never keeps showing a previous
    // session's data, or worse, a half-parsed org/role from a token that just went bad.
    //
    // Deliberately NOT clearing sessionStorage here: `isAuthenticated` can flip back
    // to true a moment later (AuthContext's refreshSession() recovers in place when
    // the refresh token is still valid), and the "restore from sessionStorage" effect
    // below then re-applies the same org/role automatically once organisations reload
    // - without that, the user was asked to re-pick org/role on every recoverable
    // session hiccup even though nothing about their membership actually changed.
    // A real logout already clears these keys itself (see AuthContext.logout).
    useEffect(() => {
        if (authLoading || isAuthenticated) {
            return;
        }
        setOrganisations([]);
        setUserRoles([]);
        setActiveOrgId(null);
        setActiveRoleId(null);
    }, [isAuthenticated, authLoading]);

    // Load organizations and roles from token
    useEffect(() => {
        // Wait for auth to complete before processing token
        if (authLoading || !isAuthenticated || !token) {
            return;
        }
        
        if (token) {
            const decoded = decodeJwt(token);
            if (decoded) {
                console.log('Full decoded token:', decoded);
                console.log('Raw roles from token:', decoded.roles);

                const orgsFromToken = decoded.organization;
                // The organization claim is only usable as a name->{id} map. If it comes back
                // as an array (e.g. a client scope mapper without "addOrganizationId" enabled
                // emits a plain array of org names), Object.entries would key it by numeric
                // index ("0", "1", ...) and every org.id would be undefined - producing a
                // dropdown that shows "0"/"1" instead of names and a profile that can never
                // match activeOrgId. Detect that and treat it as "no usable org data" instead.
                const isUsableOrgMap = orgsFromToken && typeof orgsFromToken === 'object' && !Array.isArray(orgsFromToken);
                if (orgsFromToken && !isUsableOrgMap) {
                    console.warn('Ignoring unexpected organization claim shape (expected a name->{id} map):', orgsFromToken);
                }
                const orgList = isUsableOrgMap
                    ? Object.entries(orgsFromToken)
                        .filter(([, data]) => data && typeof data === 'object' && data.id)
                        .map(([name, data]) => ({
                            id: data.id,
                            name: name,
                            displayName: name  // default; overwritten below once DB names are fetched
                        }))
                    : [];

                // A token that carries no usable organisations is treated as "no new
                // information", never as "the user lost every organisation". This effect
                // re-runs on every token rotation, including the refresh-grant tokens that
                // arrive on the first action after an idle period - and a refreshed token
                // can legitimately differ from the login token (keycloak-js sends no
                // `scope` on the refresh grant, and two client scopes both write the
                // `organization` claim). Tearing state down here left activeRoleId null, so
                // every route guard bounced to /start, and it also deleted the
                // sessionStorage fallback, making a full page reload the only recovery.
                if (orgList.length === 0) {
                    console.warn('Token carried no usable organisations - keeping the current org/role selection.');
                } else {
                    setOrganisations(orgList);

                    // A previously active org that is genuinely absent from a usable claim
                    // is dropped, so the selector cannot confirm a choice that no longer
                    // exists.
                    setActiveOrgId(prevActiveOrgId => {
                        if (prevActiveOrgId && !orgList.some(org => org.id === prevActiveOrgId)) {
                            sessionStorage.removeItem('activeOrgId');
                            sessionStorage.removeItem('activeRoleId');
                            setActiveRoleId(null);
                            return null;
                        }
                        return prevActiveOrgId;
                    });

                    // Fetch original display names from DB and merge into orgList
                    fetchOrgDisplayNames().then(dbOrgs => {
                        setOrganisations(prev => prev.map(org => {
                            const dbOrg = dbOrgs.find(d => d.keycloakOrgId === org.id);
                            return dbOrg ? { ...org, displayName: dbOrg.organisationName } : org;
                        }));
                    }).catch(err => {
                        console.warn('Could not fetch org display names:', err);
                    });
                }

                // Roles are just strings, convert them to objects
                const rolesFromToken = decoded.roles || [];
                const roleList = rolesFromToken.map(roleName => ({
                    id: roleName,  // Use the role name as the ID
                    name: roleName
                }));
                setUserRoles(roleList);
            }
        }
    }, [token, authLoading, isAuthenticated]);

    // Restore active org and role from sessionStorage after organizations are loaded
    useEffect(() => {
        if (organisations.length === 0 || activeOrgId !== null) {
            return; // Wait for orgs to load, or skip if already set
        }

        const savedOrgId = sessionStorage.getItem('activeOrgId');
        const savedRoleId = sessionStorage.getItem('activeRoleId');

        if (savedOrgId) {
            // Verify the saved org still exists in the token
            const orgExists = organisations.some(org => org.id === savedOrgId);
            if (orgExists) {
                console.log('Restoring activeOrgId from sessionStorage:', savedOrgId);
                setActiveOrgId(savedOrgId);
                
                if (savedRoleId) {
                    // Verify the saved role still exists
                    const roleExists = userRoles.some(role => role.id === savedRoleId);
                    if (roleExists) {
                        console.log('Restoring activeRoleId from sessionStorage:', savedRoleId);
                        setActiveRoleId(savedRoleId);
                    }
                }
            } else {
                // Clear invalid saved values
                sessionStorage.removeItem('activeOrgId');
                sessionStorage.removeItem('activeRoleId');
            }
        } else if (organisations.length === 1) {
            // Auto-select if only one organization
            console.log('Auto-selecting single organization:', organisations[0].id);
            setActiveOrgId(organisations[0].id);
            sessionStorage.setItem('activeOrgId', organisations[0].id);
        }
    }, [organisations, userRoles]);

    // Auto-select role if only one role matches the active organization
    useEffect(() => {
        if (!activeOrgId || activeRoleId !== null || userRoles.length === 0) {
            return;
        }

        const selectedOrg = organisations.find(org => org.id === activeOrgId);
        if (!selectedOrg) return;

        const orgSlug = normalizeOrgSlug(selectedOrg.name);
        const matchingRoles = userRoles.filter(role =>
            normalizeOrgSlug(role.name).startsWith(orgSlug + '-')
        );

        if (matchingRoles.length === 1) {
            console.log('Auto-selecting single role:', matchingRoles[0].id);
            setActiveRoleId(matchingRoles[0].id);
            sessionStorage.setItem('activeRoleId', matchingRoles[0].id);
        }
    }, [activeOrgId, userRoles, organisations, activeRoleId]);

    // Wrapper for setActiveOrgId that also saves to sessionStorage
    const handleSetActiveOrgId = (orgId) => {
        setActiveOrgId(orgId);
        if (orgId) {
            sessionStorage.setItem('activeOrgId', orgId);
        } else {
            sessionStorage.removeItem('activeOrgId');
        }
    };

    // Wrapper for setActiveRoleId that also saves to sessionStorage
    const handleSetActiveRoleId = (roleId) => {
        setActiveRoleId(roleId);
        if (roleId) {
            sessionStorage.setItem('activeRoleId', roleId);
        } else {
            sessionStorage.removeItem('activeRoleId');
        }
    };

    return (
        <OrganisationContext.Provider value={{
            organisations,
            activeOrgId,
            setActiveOrgId: handleSetActiveOrgId,
            userRoles,
            setUserRoles,
            activeRoleId,
            setActiveRoleId: handleSetActiveRoleId,
            selectorOpen,
            openSelector,
            closeSelector,
        }}>
            {children}
        </OrganisationContext.Provider>
    );
};


export const useOrganisation = () => useContext(OrganisationContext);
